import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
  Renderer2,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import {DateTime} from 'luxon';

@Component({
    selector: 'app-days-time-selection',
    templateUrl: './days-time-selection.component.html',
    styleUrls: ['./days-time-selection.component.scss'],
    standalone: false
})
export class DaysTimeSelectionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() dayTimes: DayTimes;
  @Input() selectedTime: SelectedTime;
  @Input() timezone: string | null = null;
  @Input() dstChoice: DstChoice | null = null;
  @Input() showHours = true;
  @Input() showMinutes = true;
  @Input() setTabIndexes = 1;

  @Output() timeSelected: EventEmitter<string> = new EventEmitter<string>();
  @Output() dayTimeSelected: EventEmitter<string> = new EventEmitter<string>();
  @Output() perfTimeSelected: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() focused: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() dstChoiceChange: EventEmitter<DstChoice | null> = new EventEmitter<DstChoice | null>();
  @Output() invalidDstTimeChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  isOptionsOpen: boolean = false;
  isMinutesOptionsOpen: boolean = false;
  dropdownReady = false;
  minutesDropdownReady = false;
  selectedHour: any = null;
  selectedMinute: any = null;
  availableHours: {hour: number; perfTime: boolean}[] = [];
  availableMinutes: number[] = [];
  tabKeyListener: () => void;
  hourFirstTap = true;
  minuteFirstTap = true;
  isHoursInputFocused = false;
  isMinutesInputFocused = false;
  previousHour: any = null;
  previousMinute: any = null;

  hourPressedTimes = 0;
  minutePressedTimes = 0;
  isAmbiguousDstTime = false;
  isDstTransitionDay = false;
  isNonExistingDstTime = false;
  resolvedDstSeason: DstChoice | null = null;
  selectedDstChoice: DstChoice | null = null;

  constructor(
    private renderer: Renderer2,
    private elRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.generateHoursIfNeeded();
    this.generateMinutesIfNeeded();
    this.listenForClickOutside();
    this.listenForTabKey();

    this.applySelectedTimeInput();

    this.emitSelectedTime();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTime'] && !changes['selectedTime'].firstChange) {
      // keep the displayed value in sync when the bound model changes (edit, virtual-scroll view reuse),
      // but never clobber a value the user is currently typing
      if (!this.isHoursInputFocused && !this.isMinutesInputFocused) {
        this.applySelectedTimeInput();
        this.updateDstState(false);
      }
    }
    if (changes['dayTimes'] && this.dayTimes) {
      this.generateHoursIfNeeded();
      this.generateMinutesIfNeeded();
      this.emitSelectedTime();
    }
    if (changes['dstChoice'] || changes['timezone']) {
      this.selectedDstChoice = this.dstChoice ?? null;
      this.updateDstState(false);
    }
  }

  private applySelectedTimeInput(): void {
    if (this.selectedTime?.selectedHour === '--' && this.selectedTime?.selectedMinute === '--') {
      this.selectedHour = '--';
      this.selectedMinute = '--';
    } else if (
      (this.selectedTime?.selectedHour || this.selectedTime?.selectedHour === 0) &&
      (this.selectedTime?.selectedMinute || this.selectedTime?.selectedMinute === 0)
    ) {
      this.selectedHour = Number(this.selectedTime.selectedHour);
      this.selectedMinute = Number(this.selectedTime.selectedMinute);
    } else {
      this.selectedHour = '--';
      this.selectedMinute = '--';
    }
  }

  generateHoursIfNeeded(): void {
    if (this.dayTimes) {
      this.availableHours = this.generateAvailableHours(this.dayTimes.start, this.dayTimes.end, this.dayTimes.day);
    }
  }

  resetTimes() {
    this.selectedHour = '--:--';
    this.selectedMinute = '--:--';
    this.resetDstState(true);
    this.cdr.markForCheck();
  }

  generateMinutesIfNeeded(): void {
    this.availableMinutes = [0, 15, 30, 45];
  }

  generateAvailableHours(start: any, end: any, day: Date): {hour: number; perfTime: boolean}[] {
    const hours = Array.from({length: 24}, (_, i) => i); // 0 to 23
    const availableHours = [];

    const startHour = this.extractHourFromTimeString(start);
    const endHour = this.extractHourFromTimeString(end);

    const isNextDay = startHour > endHour;

    for (const hour of hours) {
      let perfTime = false;

      if (isNextDay) {
        if (hour >= startHour || hour <= endHour) {
          perfTime = true;
        }
      } else {
        if (hour >= startHour && hour <= endHour) {
          perfTime = true;
        }
      }

      availableHours.push({hour, perfTime});
    }

    return availableHours;
  }

  extractHourFromTimeString(time: any): number {
    const [hour] = time.split(':').map(Number);
    return hour;
  }

  toggleOptions(): void {
    this.dropdownReady = false;
    this.isOptionsOpen = !this.isOptionsOpen;
    if (this.isOptionsOpen) {
      setTimeout(() => {
        this.adjustDropdownPosition('.hour-input-dropdown .options-container');
        this.dropdownReady = true;
      }, 0);
    }
  }

  toggleMinutesOptions(): void {
    this.minutesDropdownReady = false;
    this.isMinutesOptionsOpen = !this.isMinutesOptionsOpen;
    if (this.isMinutesOptionsOpen) {
      setTimeout(() => {
        this.adjustDropdownPosition('.minute-input-dropdown .options-container');
        this.minutesDropdownReady = true;
      }, 0);
    }
  }

  adjustDropdownPosition(selector: string): void {
    const dropdown = this.elRef.nativeElement.querySelector(selector);
    if (dropdown) {
      const rect = dropdown.getBoundingClientRect();
      if (rect.bottom > window.innerHeight) {
        dropdown.classList.add('dropup');
      } else {
        dropdown.classList.remove('dropup');
      }
    }
  }

  onHourSelect(event: Event, hour: {hour: number; perfTime: boolean} | null): void {
    if (!hour && hour?.hour !== 0) {
      this.selectedMinute = null;
    }
    this.selectedHour = hour ? parseInt(hour.hour.toString().padStart(2, '0'), 10) : null;

    if (event.isTrusted) {
      this.emitSelectedTime();
    }
    this.isOptionsOpen = false;
    this.focusMinuteInput();
  }

  onMinuteSelect(event: Event, minute: number | null): void {
    if (!minute && minute !== 0) {
      this.selectedHour = null;
    }
    this.selectedMinute = minute !== null ? parseInt(minute.toString().padStart(2, '0'), 10) : null;

    if (event.isTrusted) {
      this.emitSelectedTime();
    }

    this.isMinutesOptionsOpen = false;
    this.moveToNextComponentOrButton();
  }

  onHourInputChange(event: Event): void {
    const input = (event.target as HTMLInputElement).value;

    // Only trigger when two digits are entered
    const parsedHour = parseInt(input, 10);

    if (!isNaN(parsedHour) && parsedHour >= 0 && parsedHour <= 23) {
      this.selectedHour = parsedHour;
      if (input.length === 2 && event.isTrusted) {
        this.emitSelectedTime();
      }
    } else {
      this.selectedHour = null;
    }
  }

  onMinuteInputChange(event: Event): void {
    const input = (event.target as HTMLInputElement).value;

    // Only trigger when two digits are entered
    const parsedMinute = parseInt(input, 10);

    if (!isNaN(parsedMinute) && parsedMinute >= 0 && parsedMinute <= 59) {
      this.selectedMinute = parsedMinute;
      if (input.length === 2 && event.isTrusted) {
        this.emitSelectedTime();
      }
    } else {
      this.selectedMinute = null;
    }
  }

  // When the hour input is focused, reset it
  onHourFocus(isFocused: boolean): void {
    this.isHoursInputFocused = isFocused;
    this.isMinutesOptionsOpen = false;
    this.previousHour = this.selectedHour;
    this.selectedHour = null;
    this.focused.emit(true);
  }

  // When the minute input is focused, reset it
  onMinuteFocus(isFocused: boolean): void {
    this.isMinutesInputFocused = isFocused;
    this.isOptionsOpen = false;
    this.previousMinute = this.selectedMinute;
    this.selectedMinute = null;
    this.focused.emit(true);
  }

  // Restore previous value if nothing was typed
  validateHourInput(event: Event): void {
    this.isHoursInputFocused = false;
    if (this.selectedHour === null) {
      this.selectedHour = this.previousHour;
    } else if (this.selectedHour < 0 || this.selectedHour > 23) {
      this.selectedHour = null;
    } else {
      this.selectedHour = parseInt(this.selectedHour.toString().padStart(2, '0'), 10);
    }
    if (event.isTrusted && this.selectedHour !== this.previousHour) {
      this.emitSelectedTime();
    }
  }

  validateMinuteInput(event: Event): void {
    this.isMinutesInputFocused = false;
    if (this.selectedMinute === null) {
      this.selectedMinute = this.previousMinute;
    } else if (this.selectedMinute < 0 || this.selectedMinute > 59) {
      this.selectedMinute = null;
    } else {
      this.selectedMinute = parseInt(this.selectedMinute.toString().padStart(2, '0'), 10);
    }
    if (event.isTrusted && this.selectedMinute !== this.previousMinute) {
      this.emitSelectedTime();
    }
  }

  listenForTabKey() {
    this.tabKeyListener = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        this.isOptionsOpen = false;
        this.isMinutesOptionsOpen = false;
      }
    });
  }

  emitSelectedTime() {
    if (this.selectedHour === '--:--' && this.selectedMinute === '--:--') {
      this.timeSelected.emit('--:--');
      this.dayTimeSelected.emit('--:--');
      this.perfTimeSelected.emit(true);
      this.resetDstState(true);
    } else if (this.selectedHour === null && this.selectedMinute === null) {
      this.timeSelected.emit('--:--');
      this.dayTimeSelected.emit(null);
      this.perfTimeSelected.emit(true);
      this.resetDstState(true);
    } else if (
      this.selectedHour !== null &&
      this.selectedHour !== '--' &&
      this.selectedMinute !== null &&
      this.selectedMinute !== '--'
    ) {
      const formattedTime = `${this.selectedHour.toString().padStart(2, '0')}:${this.selectedMinute.toString().padStart(2, '0')}`;
      this.timeSelected.emit(formattedTime);

      const localDateTime = this.buildLocalDateTimeString(this.selectedHour, this.selectedMinute);
      this.dayTimeSelected.emit(localDateTime);

      const startHour = this.extractHourFromTimeString(this.dayTimes.start);
      const endHour = this.extractHourFromTimeString(this.dayTimes.end);

      const isNextDay = startHour > endHour;
      const isInPerfTime = isNextDay
        ? this.selectedHour >= startHour || this.selectedHour <= endHour
        : this.selectedHour >= startHour && this.selectedHour <= endHour;

      this.perfTimeSelected.emit(isInPerfTime);
      this.updateDstState(true);
    } else if (this.selectedHour !== null && this.selectedHour !== '--') {
      this.timeSelected.emit('--:--');
      this.dayTimeSelected.emit(null);
      this.perfTimeSelected.emit(true);
      this.updateDstState(false);
    } else {
      this.timeSelected.emit('--:--');
      this.dayTimeSelected.emit(null);
      this.perfTimeSelected.emit(true);
      this.resetDstState(true);
    }
  }

  selectDstChoice(choice: DstChoice): void {
    this.selectedDstChoice = choice;
    this.dstChoiceChange.emit(choice);
  }

  shouldShowDstIndicator(): boolean {
    if (
      !this.timezone ||
      typeof this.selectedHour !== 'number' ||
      typeof this.selectedMinute !== 'number' ||
      this.isNonExistingDstTime
    ) {
      return false;
    }

    return this.isAmbiguousDstTime || this.isDstTransitionDay;
  }

  getDstEmoji(season: DstChoice): string {
    return season === 'summer' ? '☀️' : '❄️';
  }

  handleHourKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const isNumberKey = event.key >= '0' && event.key <= '9';
    const isControlKey = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Shift'].includes(event.key);
    const isShiftTab = event.key === 'Tab' && event.shiftKey;

    this.hourPressedTimes++;

    if (!isNumberKey && !isControlKey) {
      event.preventDefault();
      return;
    }

    setTimeout(() => {
      const inputValue = input.value;
      if (inputValue.length === 2 || this.hourPressedTimes === 2) {
        if (parseInt(inputValue, 10) >= 0 && parseInt(inputValue, 10) <= 23) {
          this.selectedHour = parseInt(inputValue, 10);
          if (!isShiftTab && event.key !== 'Tab') {
            this.focusMinuteInput();
            this.hourPressedTimes = 0;
          }
        } else {
          input.value = '';
          this.hourPressedTimes = 0;
        }
      } else if (inputValue.length > 2) {
        input.value = inputValue.slice(0, 2);
        this.hourPressedTimes = 0;
      }
    });
  }

  handleMinuteKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const isNumberKey = event.key >= '0' && event.key <= '9';
    const isControlKey = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Shift'].includes(event.key);
    const isShiftTab = event.key === 'Tab' && event.shiftKey;

    this.minutePressedTimes++;

    if (!isNumberKey && !isControlKey) {
      event.preventDefault();
      return;
    }
    setTimeout(() => {
      const inputValue = input.value;
      if (inputValue.length === 2 || this.minutePressedTimes === 2) {
        if (parseInt(inputValue, 10) >= 0 && parseInt(inputValue, 10) <= 59) {
          this.selectedMinute = parseInt(inputValue, 10);
          if (!isShiftTab && event.key !== 'Tab') {
            this.moveToNextComponentOrButton();
            this.minutePressedTimes = 0;
          }
        } else {
          input.value = '';
          this.minutePressedTimes = 0;
        }
      } else if (inputValue.length > 2) {
        input.value = inputValue.slice(0, 2);
        this.minutePressedTimes = 0;
      }
    });
  }

  focusMinuteInput(): void {
    const minuteInput = this.elRef.nativeElement.querySelector('.minute-input');
    if (minuteInput) {
      minuteInput.value = '';
      minuteInput.focus();
    }
  }

  moveToNextComponentOrButton(): void {
    const parent = this.elRef.nativeElement.closest('.artist-row');
    if (!parent) return;

    const nextInputs = Array.from(parent.querySelectorAll('.hour-input, .minute-input, .edit-btn')) as HTMLElement[];

    const currentIndex = nextInputs.findIndex(el => el.contains(document.activeElement));

    if (currentIndex >= 0 && currentIndex + 1 < nextInputs.length) {
      const nextInput = nextInputs[currentIndex + 1] as HTMLInputElement;
      if (nextInput.classList.contains('minute-input') || nextInput.classList.contains('hour-input')) {
        nextInput.value = '';
      }
      nextInput.focus();
    }
  }

  listenForClickOutside() {
    this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const clickedInside = this.elRef.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.isOptionsOpen = false;
        this.isMinutesOptionsOpen = false;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.tabKeyListener) {
      this.tabKeyListener();
    }
  }

  private updateDstState(emitChoice = false): void {
    const minuteForDstState = this.getMinuteForDstState();
    if (!this.timezone || typeof this.selectedHour !== 'number' || minuteForDstState === null) {
      this.resetDstState(emitChoice);
      return;
    }

    const localDateTime = this.getSelectedLocalDateTime(minuteForDstState);
    if (!localDateTime) {
      this.resetDstState(emitChoice);
      return;
    }

    const possibleOffsets = localDateTime.getPossibleOffsets();
    this.isNonExistingDstTime = localDateTime.hour !== this.selectedHour || localDateTime.minute !== minuteForDstState;

    if (this.isNonExistingDstTime) {
      this.isAmbiguousDstTime = false;
      this.isDstTransitionDay = localDateTime.startOf('day').offset !== localDateTime.endOf('day').offset;
      this.resolvedDstSeason = null;
      this.selectedDstChoice = null;
      this.invalidDstTimeChange.emit(true);
      if (emitChoice) {
        this.dstChoiceChange.emit(null);
      }
      return;
    }

    this.isAmbiguousDstTime = possibleOffsets.length > 1;
    this.isDstTransitionDay = localDateTime.startOf('day').offset !== localDateTime.endOf('day').offset;

    if (!this.isAmbiguousDstTime) {
      this.resolvedDstSeason = localDateTime.isInDST ? 'summer' : 'winter';
      this.selectedDstChoice = this.resolvedDstSeason;
    } else {
      this.resolvedDstSeason = null;
      if (this.selectedDstChoice !== 'summer' && this.selectedDstChoice !== 'winter') {
        this.selectedDstChoice = this.dstChoice ?? null;
      }
    }

    if (emitChoice) {
      this.dstChoiceChange.emit(this.selectedDstChoice);
    }
    this.invalidDstTimeChange.emit(false);
  }

  private resetDstState(emitChoice = false): void {
    this.isAmbiguousDstTime = false;
    this.isDstTransitionDay = false;
    this.isNonExistingDstTime = false;
    this.resolvedDstSeason = null;
    this.selectedDstChoice = null;
    this.invalidDstTimeChange.emit(false);
    if (emitChoice) {
      this.dstChoiceChange.emit(null);
    }
  }

  private getSelectedLocalDateTime(minute: number): DateTime | null {
    if (!this.timezone || typeof this.selectedHour !== 'number') {
      return null;
    }

    const selectedDateParts = this.resolveLocalDateParts(this.selectedHour, minute);
    if (!selectedDateParts) {
      return null;
    }

    return DateTime.fromObject(
      {
        year: selectedDateParts.year,
        month: selectedDateParts.month,
        day: selectedDateParts.day,
        hour: selectedDateParts.hour,
        minute: selectedDateParts.minute,
        second: 0,
      },
      {zone: this.timezone},
    );
  }

  private getMinuteForDstState(): number | null {
    if (typeof this.selectedMinute === 'number') {
      return this.selectedMinute;
    }

    if (this.selectedMinute === null || this.selectedMinute === '--' || this.selectedMinute === '--:--') {
      return 0;
    }

    return null;
  }

  private buildLocalDateTimeString(hour: number, minute: number): string | null {
    const selectedDateParts = this.resolveLocalDateParts(hour, minute);
    if (!selectedDateParts) {
      return null;
    }

    return `${selectedDateParts.year.toString().padStart(4, '0')}-${selectedDateParts.month.toString().padStart(2, '0')}-${selectedDateParts.day
      .toString()
      .padStart(2, '0')} ${selectedDateParts.hour.toString().padStart(2, '0')}:${selectedDateParts.minute
      .toString()
      .padStart(2, '0')}:00`;
  }

  private resolveLocalDateParts(
    hour: number,
    minute: number,
  ): {year: number; month: number; day: number; hour: number; minute: number} | null {
    if (!this.dayTimes?.day) {
      return null;
    }

    const baseDay = new Date(this.dayTimes.day);
    if (Number.isNaN(baseDay.getTime())) {
      return null;
    }

    const startHour = this.extractHourFromTimeString(this.dayTimes.start);
    const endHour = this.extractHourFromTimeString(this.dayTimes.end);
    const isNextDay = startHour > endHour && hour < startHour;
    if (isNextDay) {
      baseDay.setDate(baseDay.getDate() + 1);
    }

    return {
      year: baseDay.getFullYear(),
      month: baseDay.getMonth() + 1,
      day: baseDay.getDate(),
      hour,
      minute,
    };
  }
}

export interface DayTimes {
  start: string;
  end: string;
  day: Date;
}
export interface SelectedTime {
  selectedHour: any;
  selectedMinute: any;
}

export type DstChoice = 'summer' | 'winter';
